// DynamicListingTable.tsx
import React from "react";
import { Button } from "../UI-Components/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../UI-Components/Table";
import { LuArrowDownUp, LuArrowDown, LuArrowUp } from "react-icons/lu";

// Props types for columns and actions
type ColumnDef<T> = {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  width?: string;
  cellRenderer?: (row: T) => React.ReactNode;
};

type SortConfig = { key: string; direction: "ascending" | "descending" } | null;
type ActionDef<T> = {
  name: string;
  icon: React.ReactNode;
  callback: (row: T) => void;
};

type DynamicListingTableProps<T> = {
  data: T[];
  columns: ColumnDef<T>[];
  sortConfig: SortConfig;
  onSort?: (key: string) => void;
  actions?: ActionDef<T>[];
  emptyText?: string;
};

export function DynamicListingTable<T>({
  data,
  columns,
  sortConfig,
  onSort,
  actions = [],
  emptyText = "No Records Found.",
}: DynamicListingTableProps<T>) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((col) => (
            <TableHead
              key={col.key as string}
              className={`w-[${col.width}] place-content-center`}
            >
              {col.sortable && onSort ? (
                <Button
                  variant="destructive"
                  onClick={() => onSort(col.key as string)}
                  className="flex items-center px-0"
                >
                  <div className="flex justify-between w-full items-center">
                    {col.header}
                    {sortConfig?.key === col.key &&
                    sortConfig.direction === "ascending" ? (
                      <LuArrowDown className="ml-2 h-4 w-4" />
                    ) : sortConfig?.key === col.key &&
                      sortConfig.direction === "descending" ? (
                      <LuArrowUp className="ml-2 h-4 w-4" />
                    ) : col.sortable ? (
                      <LuArrowDownUp className="ml-2 h-4 w-4" />
                    ) : null}
                  </div>
                </Button>
              ) : (
                <div className="flex justify-between w-full items-center">
                  {col.header}
                </div>
              )}
            </TableHead>
          ))}
          {actions.length > 0 && (
            <TableHead className="w-[120px] text-center">Action</TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody className="text-primary">
        {data.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={columns.length + (actions.length > 0 ? 1 : 0)}
              className="h-24 text-center"
            >
              <span>{emptyText}</span>
            </TableCell>
          </TableRow>
        ) : (
          data.map((row, idx) => (
            <TableRow key={idx}>
              {columns.map((col) => (
                <TableCell key={col.key as string}>
                  {col.cellRenderer
                    ? col.cellRenderer(row)
                    : String(row[col.key as keyof T] ?? "")}
                </TableCell>
              ))}
              {actions.length > 0 && (
                <TableCell>
                  <div className="flex justify-center">
                    {actions.map((action, i) => (
                      <Button
                        key={i}
                        variant="outline"
                        className="p-0 border-none hover:shadow-none mx-3"
                        onClick={() => action.callback(row)}
                        aria-label={action.name}
                      >
                        {action.icon}
                      </Button>
                    ))}
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
